import pipe from 'ramda/src/pipe'

import { SfLog } from '@app/adapters/logger'
import {
  castToFloat,
  castToFloatLocalized,
  parseSfZero,
} from '@app/utils/string-helpers'
import { MAX_PATTERN_LENGTH } from '../../constants'
import type {
  DateRangeRules,
  NumericRules,
  RegexpRules,
  RulesArray,
  StringLengthRules,
} from './types'
import {
  hideValidationError,
  isDateRangeRules,
  isNumericRules,
  isRegexpRules,
  isStringLengthRules,
  parseDateValue,
  showValidationError,
} from './utils'

export const validateInput = (input: HTMLInputElement): void => {
  if (!input) {
    return
  }

  const rulesString = input.getAttribute('data-live-validation-rules')
  const isRequired = input.getAttribute('data-required') === 'true'
  const isEmpty = input.value === '' || input.value === null

  if (isEmpty) {
    if (isRequired) {
      hideValidationError(input)
      showValidationError(
        input,
        window.I18n.t('liquid.input_validation.errors.required')
      )
    } else if (rulesString) {
      hideValidationError(input)
    }
    return
  }

  if (isRequired) {
    hideValidationError(input)
  }

  if (!rulesString) {
    return
  }

  const errorMessages: Array<string | undefined> = []
  let rulesArray: RulesArray

  try {
    rulesArray = JSON.parse(rulesString) as RulesArray
  } catch (e) {
    SfLog.error('Error parsing live validation rules', { error: e })
    return
  }

  const type = input?.dataset?.valueType
  for (const rules of rulesArray) {
    switch (type) {
      case 'percentage':
      case 'integer':
      case 'currency':
      case 'currency_dc': {
        if (isNumericRules(rules)) {
          const { errorMessage } = validateNumericInput(
            input,
            rules as NumericRules,
            type
          )
          if (errorMessage) errorMessages.push(errorMessage)
        }
        break
      }
      case 'date': {
        if (isDateRangeRules(rules)) {
          const { errorMessage } = validateDateInput(
            input,
            rules as DateRangeRules
          )

          if (errorMessage) errorMessages.push(errorMessage)
        }
        break
      }
      case 'text':
      case 'string': {
        if (isStringLengthRules(rules)) {
          const { errorMessage } = validateStringLengthValue(
            input.value.length,
            rules as StringLengthRules
          )
          if (errorMessage) errorMessages.push(errorMessage)
        }

        if (isRegexpRules(rules)) {
          const { errorMessage } = validateRegexpInput(
            input,
            rules as RegexpRules
          )
          if (errorMessage) errorMessages.push(errorMessage)
        }

        break
      }
    }
  }

  if (!errorMessages.length) {
    hideValidationError(input)
  } else {
    showValidationError(input, errorMessages.join('\n'))
  }
}

const validateNumericInput = (
  input: HTMLInputElement,
  rules: NumericRules,
  valueType: string
): { errorMessage?: string } => {
  const precision = parseInt(input.dataset?.precision || '2')

  const value = pipe(
    applyZeroFormatIfNeeded(input, valueType),
    castToFloatLocalizedOverride(input, valueType),
    toFixed(precision),
    parseFloat
  )(input.value)

  if (isNaN(value)) {
    return {
      errorMessage: window.I18n.t(
        'liquid.input_validation.errors.numeric.invalid'
      ),
    }
  }

  const scaleFactor = valueType === 'percentage' ? 100 : 1

  return validateNumericValue(value, precision, rules, scaleFactor)
}

const applyZeroFormatIfNeeded = (
  input: HTMLInputElement,
  valueType: string
) => {
  return (value: string): string => {
    if (valueType === 'currency' || valueType === 'currency_dc') {
      const format = input.dataset.currencyZeroFormat
      return parseSfZero(value, format)
    }
    return value
  }
}

const castToFloatLocalizedOverride = (
  input: HTMLInputElement,
  valueType: string
) => {
  return (value: string): number => {
    let options = {}

    if (valueType === 'currency' || valueType === 'currency_dc') {
      options = {
        delimiter: input.dataset.currencyDelimiter,
        separator: input.dataset.currencySeparator,
      }
    }

    return castToFloatLocalized(value, options)
  }
}

const toFixed =
  (precision: number) =>
  (value: number): string =>
    value.toFixed(precision)

const validateDateInput = (
  input: HTMLInputElement,
  rules: DateRangeRules
): { errorMessage?: string } => {
  const date = input.value
  const targetFormat =
    input.getAttribute('data-moment-js-format') ||
    (input.getAttribute('data-date-format') || '').toUpperCase()

  const parsed = parseDateValue(date, targetFormat)

  if (!parsed) {
    return {
      errorMessage: window.I18n.t(
        'liquid.input_validation.errors.date_range.invalid'
      ),
    }
  }

  return validateDateValue(parsed, targetFormat, rules)
}

const validateDateValue = (
  date: Date,
  format: string,
  rules: DateRangeRules
): {
  errorMessage?: string
} => {
  const startDate = rules.start_date
    ? parseDateValue(rules.start_date, format)
    : undefined
  const endDate = rules.end_date
    ? parseDateValue(rules.end_date, format)
    : undefined

  if (startDate && endDate) {
    if (date < startDate || date > endDate) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.date_range.between',
          { start_date: rules.start_date, end_date: rules.end_date }
        ),
      }
    }
  } else {
    if (startDate && date < startDate) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.date_range.start_date',
          { start_date: rules.start_date }
        ),
      }
    }

    if (endDate && date > endDate) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.date_range.end_date',
          { end_date: rules.end_date }
        ),
      }
    }
  }

  return { errorMessage: undefined }
}

const validateNumericValue = (
  value: number,
  precision: number,
  rules: NumericRules,
  scaleFactor: number = 1
): {
  errorMessage?: string
} => {
  const minValue =
    rules.min !== undefined
      ? parseFloat((castToFloat(rules.min) * scaleFactor).toFixed(precision))
      : undefined
  const maxValue =
    rules.max !== undefined
      ? parseFloat((castToFloat(rules.max) * scaleFactor).toFixed(precision))
      : undefined
  const minExclusiveValue =
    rules.min_exclusive !== undefined
      ? parseFloat(
          (castToFloat(rules.min_exclusive) * scaleFactor).toFixed(precision)
        )
      : undefined
  const maxExclusiveValue =
    rules.max_exclusive !== undefined
      ? parseFloat(
          (castToFloat(rules.max_exclusive) * scaleFactor).toFixed(precision)
        )
      : undefined

  if (minValue !== undefined && maxValue !== undefined) {
    if (value < minValue || value > maxValue) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.numeric.between',
          { min: minValue, max: maxValue }
        ),
      }
    }
  } else {
    if (minValue !== undefined && value < minValue) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.numeric.min',
          {
            min: minValue,
          }
        ),
      }
    }

    if (maxValue !== undefined && value > maxValue) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.numeric.max',
          {
            max: maxValue,
          }
        ),
      }
    }
  }

  if (minExclusiveValue !== undefined && maxExclusiveValue !== undefined) {
    if (value <= minExclusiveValue || value >= maxExclusiveValue) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.numeric.between_exclusive',
          { min_exclusive: minExclusiveValue, max_exclusive: maxExclusiveValue }
        ),
      }
    }
  } else {
    if (minExclusiveValue !== undefined && value <= minExclusiveValue) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.numeric.min_exclusive',
          { min_exclusive: minExclusiveValue }
        ),
      }
    }

    if (maxExclusiveValue !== undefined && value >= maxExclusiveValue) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.numeric.max_exclusive',
          { max_exclusive: maxExclusiveValue }
        ),
      }
    }
  }

  return { errorMessage: undefined }
}

const validateStringLengthValue = (
  stringLength: number,
  rules: StringLengthRules
): {
  errorMessage?: string
} => {
  const minLength =
    rules.min_length !== undefined ? parseInt(rules.min_length) : undefined
  const maxLength =
    rules.max_length !== undefined ? parseInt(rules.max_length) : undefined

  if (minLength !== undefined && maxLength !== undefined) {
    if (stringLength < minLength || stringLength > maxLength) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.length.between',
          { min_length: minLength, max_length: maxLength }
        ),
      }
    }
  } else {
    if (minLength !== undefined && stringLength < minLength) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.length.min_length',
          {
            min_length: minLength,
          }
        ),
      }
    }

    if (maxLength !== undefined && stringLength > maxLength) {
      return {
        errorMessage: window.I18n.t(
          'liquid.input_validation.errors.length.max_length',
          {
            max_length: maxLength,
          }
        ),
      }
    }
  }

  return { errorMessage: undefined }
}

const validateRegexpInput = (
  input: HTMLInputElement,
  rules: RegexpRules
): {
  errorMessage?: string
} => {
  const value = input.value
  const pattern = rules.pattern
  const validationText = rules.validation_text
  const patternFlags = rules.pattern_flags

  if (!pattern) return { errorMessage: undefined }

  if (pattern.length > MAX_PATTERN_LENGTH) {
    return {
      errorMessage: window.I18n.t(
        'liquid.input_validation.errors.regexp.invalid_pattern_length',
        { max_length: MAX_PATTERN_LENGTH }
      ),
    }
  }

  try {
    const result = new RegExp(pattern, patternFlags).test(value)

    if (!result) {
      return {
        errorMessage:
          validationText ??
          window.I18n.t('liquid.input_validation.errors.regexp.invalid_input', {
            pattern,
          }),
      }
    }

    return { errorMessage: undefined }
  } catch (error) {
    const errorMessage = (error as Error)?.message ?? String(error)

    return {
      errorMessage: window.I18n.t(
        'liquid.input_validation.errors.regexp.invalid_pattern',
        { pattern, error_message: errorMessage }
      ),
    }
  }
}
